import styles from "./DealerAnalytics.module.scss";
import { ColumnChart, Legend } from "./charts";
import { Card, Delta, Empty, ReportFrame, SortTable, StatTile, fmtDate, fmtMonthKey, fmtNum, fmtUsd, fmtUsdCompact, useReport } from "./shared";

const SOURCE_LABELS = { "dealer-stock": "Dealer stock", "current-inventory": "In the US", auction: "Auction win" };

const MONTH_SERIES = [
  { key: "dealerValue", label: "Dealer sales", color: "var(--da-s1)" },
  { key: "auctionValue", label: "Auction wins", color: "var(--da-s2)" },
  { key: "collected", label: "Money collected", color: "var(--da-s3)" },
];

const Sales = ({ range, refreshKey }) => {
  const state = useReport("sales", range, refreshKey);
  return (
    <ReportFrame state={state}>
      {(d) => {
        const dealer = (list) => list.filter((s) => s.source !== "auction");
        const count = (list) => dealer(list).reduce((t, s) => t + s.count, 0);
        const value = (list) => dealer(list).reduce((t, s) => t + s.value, 0);
        return (
          <div className={styles.stack}>
            <div className={styles.tiles}>
              <StatTile label="Dealer sales" value={fmtNum(count(d.current))} delta={<Delta current={count(d.current)} previous={count(d.previous)} />} sub="dealer stock and cars in the US" />
              <StatTile label="Sales value" value={fmtUsd(value(d.current))} delta={<Delta current={value(d.current)} previous={value(d.previous)} />} sub="agreed prices" />
              <StatTile label="Average sale" value={fmtUsd(d.averageSale)} sub="dealer sales this period" />
              <StatTile label="Lead to sale" value={d.medianLeadToSaleDays === null ? "–" : `${d.medianLeadToSaleDays} days`} sub="median, sales linked to a lead" />
              <StatTile label="Outstanding" value={fmtUsd(d.invoices.outstanding)} sub={`${fmtNum(d.invoices.outstandingCount)} invoices · ${fmtUsd(d.invoices.overdue)} overdue`} />
            </div>

            <Card title="Month by month" sub="Value of sales recorded each month, and money received (payments plus deposits). Last 12 months.">
              <Legend items={MONTH_SERIES} />
              <div style={{ marginTop: 10 }}>
                <ColumnChart rows={d.monthly.map((m) => ({ ...m, label: fmtMonthKey(m.month) }))} series={MONTH_SERIES} format={fmtUsdCompact} />
              </div>
            </Card>

            <div className={styles.grid2}>
              <Card title="Where every open car is" sub="Purchases not yet completed, by their current step, and how long they have sat there.">
                <SortTable
                  rowKey={(r) => r.step}
                  rows={d.pipeline}
                  empty="No open purchases."
                  columns={[
                    { key: "step", label: "Step", sortable: false },
                    { key: "count", label: "Cars", num: true, bar: true },
                    { key: "value", label: "Value", num: true, render: (r) => fmtUsd(r.value) },
                    { key: "medianDays", label: "Median days here", num: true },
                  ]}
                />
              </Card>
              <Card title="By source" sub="This period against the one before.">
                <SortTable
                  rowKey={(r) => r.source}
                  rows={d.current.map((c, i) => ({ ...c, prevCount: d.previous[i].count, prevValue: d.previous[i].value }))}
                  columns={[
                    { key: "source", label: "Source", render: (r) => SOURCE_LABELS[r.source] || r.source },
                    { key: "count", label: "Sales", num: true },
                    { key: "value", label: "Value", num: true, render: (r) => fmtUsd(r.value) },
                    { key: "prevCount", label: "Previous", num: true },
                    { key: "prevValue", label: "Previous value", num: true, render: (r) => fmtUsd(r.prevValue) },
                  ]}
                />
              </Card>
            </div>

            <Card title="Latest sales">
              {d.recent.length ? (
                <SortTable
                  rowKey={(r) => r._id}
                  rows={d.recent}
                  columns={[
                    {
                      key: "title",
                      label: "Car",
                      render: (r) => (
                        <span className={styles.carCell}>
                          {r.imageURL ? <img className={styles.thumb} src={r.imageURL} alt="" loading="lazy" referrerPolicy="no-referrer" /> : <span className={styles.thumb} />}
                          {r.title}
                        </span>
                      ),
                    },
                    { key: "buyer", label: "Buyer" },
                    { key: "source", label: "Source", render: (r) => SOURCE_LABELS[r.source] || r.source },
                    { key: "price", label: "Price", num: true, render: (r) => fmtUsd(r.price) },
                    { key: "status", label: "Step" },
                    { key: "createdAt", label: "Recorded", num: true, sort: (r) => new Date(r.createdAt).getTime(), render: (r) => fmtDate(r.createdAt) },
                  ]}
                />
              ) : <Empty>No sales recorded yet.</Empty>}
            </Card>
          </div>
        );
      }}
    </ReportFrame>
  );
};

export default Sales;
